import { WarningOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row } from 'antd';
import { FormInstance } from 'antd/lib/form';
import moment from 'moment';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { SHORT_DATE_TIME_FORMAT } from '../../../utils/date';
import { formatDateToUTC } from '../../../utils/dateFormatter';
import {
  ErrorMessage,
  StyledAlert,
  StyledDatePicker,
  StyledFormItem,
  Title,
} from './style';

interface Props {
  startDate: any;
  endDate: any;
  status?: string;
  apiKey?: string;
  onEdit: Function;
  onCancel: Function;
  isLoading?: boolean;
}

const EditAPIKeyInstanceForm = ({
  startDate,
  endDate,
  status,
  apiKey,
  onEdit,
  onCancel,
  isLoading = false,
}: Props) => {
  const formRef = useRef<FormInstance>(null);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    activeFrom: moment(startDate),
    expiresAt: moment(endDate),
  };

  const isCurrentlyActive = status === 'Active';
  const disabledStartDate = (current: any) =>
    current && current.isBefore(moment(), 'day');
  const disabledEndDate = (current: any) =>
    current && current <= moment().endOf('day');

  const [currentValues, setCurrentValues] = useState(initialValues);
  const [isValidDateRange, setIsValidDateRange] = useState(true);
  const [newStartDateStatus, setNewStartDateStatus] = useState('');

  const onChangeActiveDate = (newActiveDate: any) => {
    if (moment(newActiveDate).isSameOrBefore(new Date(), 'day')) {
      setNewStartDateStatus('active');
    } else {
      setNewStartDateStatus('inactive');
    }
  };

  const validateRange = (dateRange: any) => {
    const { activeFrom, expiresAt } = dateRange;
    return moment(expiresAt).isAfter(activeFrom, 'day');
  };

  const onConfirm = async (values: any) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      let payload = {};

      if (formatDateToUTC(startDate) !== formatDateToUTC(values.activeFrom)) {
        payload = {
          ...payload,
          activeFrom: values.activeFrom,
        };
      }

      if (formatDateToUTC(endDate) !== formatDateToUTC(values.expiresAt)) {
        payload = {
          ...payload,
          expireAt: values.expiresAt,
        };
      }

      await onEdit(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      layout="vertical"
      initialValues={initialValues}
      ref={formRef}
      form={form}
      onFinish={values => onConfirm(values)}
      onValuesChange={(changedValue, allValues) => {
        setCurrentValues(allValues);
        setIsValidDateRange(validateRange(allValues));

        if (changedValue.activeFrom) {
          onChangeActiveDate(changedValue.activeFrom);
        }
      }}
      className="custom-apikey"
    >
      <Title>
        <FormattedMessage id="ApiKeys.activeKeyModal.title" />
      </Title>

      <Row justify="center">
        {isCurrentlyActive ? (
          <p>
            <FormattedMessage id="ApiKeys.activeKeyModal.subTitle" />
          </p>
        ) : (
          <p>
            <FormattedMessage id="ApiKeys.deactivateKeyModal.subTitle" />
          </p>
        )}
      </Row>

      <Row justify="center">
        <p className="text-wrap">
          <FormattedMessage id="ApiKeys.modal.label" />
          {apiKey}
        </p>
      </Row>

      <Row justify="center" gutter={[10, 0]} style={{ marginBottom: '15px' }}>
        <Col span={9}>
          <StyledFormItem
            label={<FormattedMessage id="ApiKeys.modal.label.activefrom" />}
            name="activeFrom"
          >
            <StyledDatePicker
              format={SHORT_DATE_TIME_FORMAT}
              showTime
              disabledDate={disabledStartDate}
              data-testid="api-key-instance_activeFrom"
            />
          </StyledFormItem>
        </Col>
        <Col span={9}>
          <StyledFormItem
            label={<FormattedMessage id="ApiKeys.modal.label.activeuntil" />}
            name="expiresAt"
          >
            <StyledDatePicker
              format={SHORT_DATE_TIME_FORMAT}
              showTime
              disabledDate={disabledEndDate}
              data-testid="api-key-instance_activeUntil"
            />
          </StyledFormItem>
        </Col>
        {!isValidDateRange && (
          <Col span={18}>
            <ErrorMessage>
              <span className="icon-wraper">
                <WarningOutlined data-testid="WarningOutlinedIcon" />
              </span>
              <FormattedMessage id="ApiKeys.data.error" />
            </ErrorMessage>
          </Col>
        )}
      </Row>

      {isValidDateRange && (
        <Row justify="center">
          <Col span={18}>
            {!isCurrentlyActive && newStartDateStatus === 'active' && (
              <StyledAlert
                message="This API key will become active now"
                type="warning"
                showIcon
              />
            )}

            {isCurrentlyActive && newStartDateStatus === 'inactive' && (
              <StyledAlert
                message="This API key will stop to be active"
                description="If you proceed, this API key will no longer be usable. It will become active again on the date  entered above."
                type="warning"
                showIcon
              />
            )}
          </Col>
        </Row>
      )}

      <Row justify="center" gutter={[15, 0]}>
        <Col>
          <Button
            type="default"
            shape="round"
            size="large"
            onClick={() => onCancel()}
          >
            <FormattedMessage id="Global.cancel" />
          </Button>
        </Col>
        <Col>
          <Button
            disabled={
              !currentValues.activeFrom ||
              !currentValues.expiresAt ||
              !isValidDateRange ||
              isSubmitting ||
              isLoading
            }
            type="primary"
            shape="round"
            size="large"
            htmlType="submit"
          >
            <FormattedMessage id="Global.confirm" />
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

EditAPIKeyInstanceForm.defaultProps = {
  status: 'Active',
  apiKey: '',
};

export default EditAPIKeyInstanceForm;
